export const ORDERED_STRATEGIES = ['linear', 'rrordered'];

const MAX_PENALTY = 2147483647;

export const compareExt = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true });

export const isOrderedStrategy = (strategy) => ORDERED_STRATEGIES.includes(String(strategy || ''));

export const isPenaltyValid = (value) => /^\d+$/.test(String(value)) && Number(value) <= MAX_PENALTY;

const penaltyNumber = (value) => (value == null ? 0 : Number(value));

const isPaused = (member) => member.paused != null && Number(member.paused) !== 0;

const toDraftMember = (member) => ({
  ext: String(member.ext),
  name: member.name == null ? null : member.name,
  penalty: String(penaltyNumber(member.penalty)),
  paused: member.paused == null ? null : member.paused,
  invalidEndpoint: Boolean(member.invalidEndpoint),
});

const toAvailable = (entry) => ({ ext: String(entry.ext), name: entry.name == null ? null : entry.name });

const sortAvailable = (list) => [...list].sort((a, b) => compareExt(a.ext, b.ext));

export const createDraft = (roster) => {
  const members = (roster.members || []).map(toDraftMember);
  return {
    loaded: {
      exts: members.map((member) => member.ext),
      penalties: new Map(members.map((member) => [member.ext, penaltyNumber(member.penalty)])),
    },
    members,
    available: sortAvailable((roster.available || []).map(toAvailable)),
    removedInvalid: [],
  };
};

export const addExts = (draft, exts) => {
  const wanted = new Set(exts);
  const moving = [];
  for (const ext of exts) {
    const entry = draft.available.find((candidate) => candidate.ext === ext);
    if (entry && !moving.includes(entry)) moving.push(entry);
  }
  if (moving.length === 0) return draft;
  return {
    ...draft,
    members: [
      ...draft.members,
      ...moving.map((entry) => ({ ext: entry.ext, name: entry.name, penalty: '0', paused: null, invalidEndpoint: false })),
    ],
    available: draft.available.filter((entry) => !wanted.has(entry.ext)),
  };
};

export const removeExts = (draft, exts) => {
  const wanted = new Set(exts);
  const leaving = draft.members.filter((member) => wanted.has(member.ext));
  if (leaving.length === 0) return draft;
  return {
    ...draft,
    members: draft.members.filter((member) => !wanted.has(member.ext)),
    available: sortAvailable([
      ...draft.available,
      ...leaving.filter((member) => !member.invalidEndpoint).map(toAvailable),
    ]),
    removedInvalid: [...draft.removedInvalid, ...leaving.filter((member) => member.invalidEndpoint).map((member) => member.ext)],
  };
};

const swapMembers = (draft, ext, delta) => {
  const index = draft.members.findIndex((member) => member.ext === ext);
  const target = index + delta;
  if (index === -1 || target < 0 || target >= draft.members.length) return draft;
  const members = [...draft.members];
  [members[index], members[target]] = [members[target], members[index]];
  return { ...draft, members };
};

export const moveUp = (draft, ext) => swapMembers(draft, ext, -1);

export const moveDown = (draft, ext) => swapMembers(draft, ext, 1);

export const setPenalty = (draft, ext, value) => ({
  ...draft,
  members: draft.members.map((member) => (member.ext === ext ? { ...member, penalty: String(value) } : member)),
});

export const hasInvalidPenalty = (draft) => draft.members.some((member) => !isPenaltyValid(member.penalty));

const wasLoaded = (draft, ext) => draft.loaded.penalties.has(ext);

const penaltyChanged = (draft, member) =>
  wasLoaded(draft, member.ext) && (!isPenaltyValid(member.penalty) || Number(member.penalty) !== draft.loaded.penalties.get(member.ext));

const changes = (draft) => {
  const currentExts = draft.members.map((member) => member.ext);
  const current = new Set(currentExts);
  const added = currentExts.filter((ext) => !wasLoaded(draft, ext));
  const removed = draft.loaded.exts.filter((ext) => !current.has(ext));
  const penalties = draft.members.filter((member) => penaltyChanged(draft, member)).map((member) => member.ext);
  const keptLoaded = draft.loaded.exts.filter((ext) => current.has(ext));
  const keptCurrent = currentExts.filter((ext) => wasLoaded(draft, ext));
  const orderChanged = keptLoaded.some((ext, index) => keptCurrent[index] !== ext);
  return { added, removed, penalties, orderChanged };
};

export const isDirty = (draft) => {
  const { added, removed, penalties, orderChanged } = changes(draft);
  return added.length > 0 || removed.length > 0 || penalties.length > 0 || orderChanged;
};

export const summarize = (draft, strategy) => {
  const { added, removed, penalties, orderChanged } = changes(draft);
  const parts = [];
  if (added.length) parts.push(`+${added.length} adicionados`);
  if (removed.length) parts.push(`−${removed.length} removidos`);
  if (penalties.length) parts.push(`${penalties.length} prioridades alteradas`);
  if (orderChanged && isOrderedStrategy(strategy)) parts.push('ordem alterada');
  return parts.join(', ');
};

export const tagsFor = (draft, item, side) => {
  if (side === 'right') return wasLoaded(draft, item.ext) ? ['Removido'] : [];
  const tags = [];
  if (!wasLoaded(draft, item.ext)) tags.push('Novo');
  if (item.invalidEndpoint) tags.push('Ramal inexistente');
  if (penaltyChanged(draft, item)) tags.push('Alterado');
  return tags;
};

export const buildSyncPayload = (draft, { queueName, etag }) => ({
  queueName,
  expectedEtag: etag,
  members: draft.members.map((member, index) => ({
    ext: member.ext,
    penalty: Number(member.penalty),
    member_order: index,
  })),
});

export const pausedAmong = (draft, exts) => {
  const wanted = new Set(exts);
  return draft.members.filter((member) => wanted.has(member.ext) && isPaused(member)).map((member) => member.ext);
};

export const applyQuickInput = (draft, parsed) => {
  const availableExts = new Set(draft.available.map((entry) => entry.ext));
  const memberExts = new Set(draft.members.map((member) => member.ext));
  const added = [];
  const alreadyIn = [];
  const notTenant = [];
  for (const ext of parsed.exts) {
    if (availableExts.has(ext)) added.push(ext);
    else if (memberExts.has(ext)) alreadyIn.push(ext);
    else notTenant.push(ext);
  }
  return { draft: addExts(draft, added), added, alreadyIn, notTenant, invalid: parsed.invalidTokens };
};
